/**
 * Download print-ready card artwork for a commercial printer.
 *
 * The browser's own print action (`print-card.js`) puts a card on whatever paper
 * is loaded, which is the right answer for a family with a printer. This produces
 * the other thing: a fixed 91×61mm two-page PDF with bleed, CMYK and embedded
 * fonts, which is what a trade printer needs. See `docs/doxzoo.md`.
 *
 * Deliberately thin. Everything that makes the PDF — pdf-lib, fontkit, the QR
 * encoder, the fonts — lives behind the dynamic `import()` below, so this module
 * can be imported freely from the pages without any of that weight reaching the
 * main bundle.
 *
 * The phrase never leaves the browser. There is no service call on this path at
 * all, which is what keeps a card obtainable by an account whose payment has
 * lapsed — §12 is explicit that a payment problem must not cut a family off from
 * contacts they already have.
 */

/**
 * Hand a generated file to the browser as a download.
 *
 * Mirrors `downloadJson` in `src/pages/your-data.js`, with one change: the object
 * URL is revoked on a later task rather than immediately after `click()`. Revoking
 * synchronously is safe for a few kilobytes of JSON and races the download for
 * anything larger.
 *
 * A download is not a fetch or a navigation, so no CSP fetch-directive applies —
 * which is why this works under a policy that has no `blob:` anywhere in it. Do
 * not try to *preview* the PDF: `img-src` has no `blob:`, `object-src` is `none`
 * and `frame-src` allows only reCAPTCHA, so every preview route is blocked.
 *
 * @param {Uint8Array} bytes
 * @param {string} fileName
 * @returns {void}
 */
function offerDownload(bytes, fileName) {
  const blob = new Blob([/** @type {BlobPart} */ (bytes)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * Generate and download the print file for a profile.
 *
 * @param {{ label: string, phrase: string }} profile
 * @returns {Promise<void>}
 */
export async function downloadPrintFile(profile) {
  const { createPrintFile } = await import("./card-pdf/browser.js");
  const { bytes, fileName } = await createPrintFile(profile);
  offerDownload(bytes, fileName);
}
